import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Paper, Stack, IconButton, Grid, InputAdornment } from '@mui/material';
import { useFormikContext, Formik, Form, Field, FieldArray } from 'formik';
import DeleteIcon from '@mui/icons-material/Delete';

import useConfig from '../../context/ConfigContext';

import {
  AutoCompleteSelectUI,
  ButtonUI,
  CheckboxUI,
  DialogUI,
  FileUploadDialogUI,
  FormErrorUI,
  FormLabelUI,
  InputUI,
} from '../Interface';
import FormLabelUi from '../Interface/FormLabelUi';
import AddButtonUI from '../Interface/AddButtonUI';
import { v4 as uuidv4 } from 'uuid';

const SourceListMachineDialogV2 = ({
  open,
  onClose,
  maxWidth,
  id,
  selectedValues,
  autocompleteOptions,
  resourceOptions,
  valueIndex,
  defualtOperationMachine,
}) => {
  const { values, errors, setFieldValue, validateField } = useFormikContext();
  const { config, getConfig } = useConfig();
  const [savedValues, setSavedValues] = useState([]);

  useEffect(() => {
    if (Object.keys(config).length === 0) getConfig();
  }, []);

  useEffect(() => {
    if (open) {
      setSavedValues(values.sourceListOperation[valueIndex].sourceListOperationMachine);
    }
  }, [open]);

  const getMachineIndex = (values, id) => {
    return values.sourceListOperation[valueIndex].sourceListOperationMachine.findIndex((rc) => rc.id === id);
  };

  const handleAdd = (push) => () => {
    const newLine = {
      ...defualtOperationMachine,
      id: uuidv4(),
    };
    push(newLine);
  };

  const qtyUnit = autocompleteOptions?.quantityUnit?.find((r) => r.value === config?.defOperationQtyUnit)?.label;

  const handleCloseClick = () => {
    // kimazsolázni a workflow-ban létrehozott sorkat, és kibaszni őkét
    setFieldValue(`sourceListOperation.${valueIndex}.sourceListOperationMachine`, savedValues);
    onClose();
  };

  return (
    <DialogUI
      open={open}
      onClose={() => {}}
      headerContent={'Erőforrások'}
      fullWidth={true}
      maxWidth={maxWidth}
      contentStyle={{ bgcolor: 'grey.100', p: 1 }}
    >
      <FieldArray name={`sourceListOperation.${valueIndex}.sourceListOperationMachine`}>
        {({ remove, push }) => (
          <>
            <Box mb={2}>
              <Typography variant="h6" gutterBottom align="center" sx={{ my: 1 }}>
                {
                  autocompleteOptions?.operation?.find(
                    (r) => r.value === values?.sourceListOperation?.[valueIndex]?.operationId
                  )?.label
                }
              </Typography>
            </Box>
            <Box>
              <AddButtonUI onClick={handleAdd(push)} />
            </Box>

            {values.sourceListOperation?.[valueIndex]?.sourceListOperationMachine.map((item, idx) => {
              const machineIndex = getMachineIndex(values, item.id);
              const findedOperation = autocompleteOptions.resource.find(
                (r) =>
                  r.value ===
                  values.sourceListOperation?.[valueIndex]?.sourceListOperationMachine[machineIndex]?.resourceId
              );

              return (
                <>
                  <Paper key={item.id ?? idx} variant="outlined" sx={{ p: 1, mt: 0.5 }}>
                    <Typography variant="subtitle2">#{idx + 1}</Typography>

                    <Grid container spacing={1} alignItems="flex-start">
                      <Grid item xs={8}>
                        <Field
                          type="autocomplete"
                          name={`sourceListOperation.${valueIndex}.sourceListOperationMachine.${machineIndex}.resourceId`}
                          component={AutoCompleteSelectUI}
                          label={<FormLabelUi text="Erőforrás" />}
                          fullWidth
                          variant="standard"
                          helperText={
                            <FormErrorUI
                              message={
                                errors.sourceListOperation?.[valueIndex]?.sourceListOperationMachine?.[machineIndex]
                                  ?.resourceId
                              }
                            />
                          }
                          InputLabelProps={{
                            shrink:
                              values.sourceListOperation?.[valueIndex]?.sourceListOperationMachine?.[machineIndex]
                                ?.resourceId !== '',
                          }}
                          onChange={async (_e, newVal) => {
                            await setFieldValue(
                              `sourceListOperation.${valueIndex}.sourceListOperationMachine.${machineIndex}.resourceId`,
                              newVal?.value ?? null
                            );
                            await validateField(
                              `sourceListOperation.${valueIndex}.sourceListOperationMachine.${machineIndex}.resourceId`
                            );
                          }}
                          onBlur={async () =>
                            await validateField(
                              `sourceListOperation.${valueIndex}.sourceListOperationMachine.${machineIndex}.resourceId`
                            )
                          }
                          dataset={resourceOptions}
                          selectedLabelValue={findedOperation?.label}
                          selectedValue={
                            values.sourceListOperation?.[valueIndex].sourceListOperationMachine?.[machineIndex]
                              ?.resourceId
                          }
                        />
                      </Grid>

                      <Grid item xs={3}>
                        <Field
                          type="number"
                          as={InputUI}
                          variant="standard"
                          label={<FormLabelUi text="Ráfordítás" />}
                          name={`sourceListOperation.${valueIndex}.sourceListOperationMachine.${machineIndex}.neededQuantity`}
                          onFocus={(e) => e.target.select()}
                          onClick={(e) => e.target.select()}
                          onChange={(e) => {
                            setFieldValue(
                              `sourceListOperation.${valueIndex}.sourceListOperationMachine.${machineIndex}.neededQuantity`,
                              e.target.value ?? ''
                            );
                          }}
                          helperText={
                            <FormErrorUI
                              message={
                                errors?.sourceListOperation?.[valueIndex]?.sourceListOperationMachine?.[machineIndex]
                                  ?.neededQuantity
                              }
                            />
                          }
                          fullWidth
                          InputProps={{ endAdornment: <InputAdornment position="end">{qtyUnit}</InputAdornment> }}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton onClick={() => remove(machineIndex)} sx={{ mt: 1 }}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                </>
              );
            })}
          </>
        )}
      </FieldArray>
      <Box display="flex" gap="0.5rem" justifyContent="center" mt="1rem">
        <ButtonUI
          text="Mentés"
          className="bg-success"
          size="sm"
          onClick={() => {
            onClose();
          }}
        />
        <ButtonUI
          text="Bezárás"
          className="bg-primary"
          size="sm"
          onClick={() => {
            handleCloseClick();
          }}
        />
      </Box>
    </DialogUI>
  );
};

export default SourceListMachineDialogV2;
